import moment from 'moment';
import find from 'lodash/find';

import { DATE } from 'utils/fieldTypes';
import { setTime } from 'utils/time';
import { BaseOption } from 'ui/selects';
import { ESTypeMap, TimeOfDay, DelayNodeData } from 'modules/workflows/types';
import { getESMappingsSelectOptions } from 'modules/workflows/utils';
import { matchFilter } from 'ui/selects/hooks/useCategorySelection.utils';
import { removeEmptyDeep } from 'modules/workflows/components/NodeEditor/utils';
import { omitDeep } from 'deepdash/standalone';
import { DelayTimeConfig } from './DelayEditor.types';
import {
  DELAY_DATA_TYPES,
  CONTACT_PROPERTY_FIELD,
  DELAY_USING_PROPERTY_OPTION_VALUE_SEPARATOR,
} from './DelayEditor.constants';

export const getPlural = (num: number | void, singular: string, plural?: string): string =>
  Number(num) === 1 ? singular : plural || `${singular}s`;

export const isValidNumber = (value: any, opts: DelayTimeConfig = {}): boolean => {
  const num = Number(value);

  if (Number.isNaN(num)) return false;

  const { min = -Infinity, max = Infinity } = opts;

  return value >= min && value <= max;
};

export const convertTodTimeToString = (todTime: any): string => {
  const time = Number(todTime);

  if (Number.isNaN(time)) return '00';

  return time < 10 ? `0${time}` : `${time}`;
};

export const getTodTimestamp = (timeOfDay?: TimeOfDay) =>
  timeOfDay?.timestamp ? moment.utc(timeOfDay.timestamp).format(moment.HTML5_FMT.DATE) : undefined;

export const getNumber = (input?: string | number) => {
  const num = Number(input);

  return Number.isNaN(num) ? undefined : num;
};

export const getTodTime = (hr = 12, minute?: number): string => {
  const hours = convertTodTimeToString(hr);
  const minutes = convertTodTimeToString(minute);
  return `${hours}:${minutes}`;
};

export const parseTodTime = (time?: string) => {
  if (!time) return {};

  const [hour, minute] = time.split(':');

  return { hour: Number(hour), minute: Number(minute) };
};

export const parseTodDate = (date: string, time: string) =>
  date ? setTime(moment(date).utc(), time).valueOf() : null;

/**
 * Parse ES Types into options that are able to be rendered by the select dropdowns
 */
export const parseEsMappings = (mappings: ESTypeMap) =>
  getESMappingsSelectOptions(mappings, {
    showDataType: ({ dataType }) => DELAY_DATA_TYPES.includes(dataType),
    showField: ({ fieldType }) => fieldType === DATE,
  });

export const getDefaultDataType = (dataTypes: BaseOption[] = []) =>
  find(dataTypes, { value: CONTACT_PROPERTY_FIELD })?.value || dataTypes[0]?.value;

export const getDelayUsingPropertyValue = (dataType: string, field: string) =>
  `${dataType}${DELAY_USING_PROPERTY_OPTION_VALUE_SEPARATOR}${field}`;

/**
 * Returns a flat array representation of of options by categories
 */
export const getDelayUsingPropertyOptions = (
  categories: BaseOption[],
  optionsByCategories: Record<string, BaseOption[]>,
  filter: string,
) => {
  return categories.reduce((acc: BaseOption[], category: BaseOption) => {
    const categoryOptions = optionsByCategories[category.value]
      .filter((option) => matchFilter(filter, option)) // apply query
      .map((option) => ({
        ...option,
        value: getDelayUsingPropertyValue(category.value, option.value), // prefixes field value with category
      }));

    //  If any items matches the `query`, return category and the items, otherwise return nothing.
    const result = categoryOptions.length > 0 ? [category, ...categoryOptions] : [];

    return [...acc, ...result];
  }, []);
};

/**
 * Get comparable values from a given DelayEditor nodeData object.
 * - Only returns fields that are affected by UI changes
 *
 * @param { Object } data
 * @returns comparable nodeData object
 */
export const getDelayEditorNodeDataToBeCompared = (data: Partial<DelayNodeData>) => {
  // GraphQL keys to be removed
  const keysToRemove = ['__typename'];
  const cleanedData = omitDeep(removeEmptyDeep(data), keysToRemove);

  const selectedSkipDaysOfWeek = Object.entries(cleanedData?.skipDaysOfWeek || {}).filter(
    ([, value]) => value,
  );

  // If no days were selected by the user and are just initial values from the form
  // we set this to `null` because intial values is equivalent to that
  if (cleanedData?.skipDaysOfWeek && selectedSkipDaysOfWeek.length === 0) {
    cleanedData.skipDaysOfWeek = null;
  }

  return {
    delayUsingProperty: cleanedData?.delayUsingProperty ?? undefined,
    delayDays: cleanedData?.delayDays ?? 0,
    delayHours: cleanedData?.delayHours ?? 0,
    delayMinutes: cleanedData?.delayMinutes ?? 0,

    timeOfDay: cleanedData?.timeOfDay ?? undefined,

    skipDaysOfWeek: cleanedData?.skipDaysOfWeek ?? undefined,
  };
};
